import { useCallback, useEffect, useRef, useState } from 'react';
import './WallSetupForm.scss';
import SceneManager from '../../../three/scenes/SceneManager';
import DragController from '../../../hooks/DragController';
import { Col, Row } from 'antd';
import ButtonPrimaryLarge from '../../common/ButtonPrimary/ButtonPrimaryLarge';
import { Icon } from '../../icon/icon';
import { Form } from 'antd';
import InputComponent from '../../common/InputComponent/InputComponent';
import CustomProductCard from './CustomProductCard';
import BottomSheet from '../../common/BottomSheet/BottomSheet';
import { roundTo } from '../../../utils/geometry/epsilon';
import SelectComponent from '../../common/SelectComponent/SelectComponent';
import ColorPicker from '../../common/ColorPicker/ColorPicker';
import callApi from '../../../utils/api/callAPI';
import { captureRoomScreenshot } from '../../../utils/canvas/canvasScreenshot';
import ButtonIconOutlineLarge from '../../common/ButtonIconOutline/ButtonIconOutlineLarge';
import { Divider } from 'antd';
import {
  evaluateExpression,
  getInputType,
} from '../../../utils/string/inputExpression';
import { openMessageSuccess } from '../../common/Message/openMessageSuccess';
import { openMessageError } from '../../common/Message/openMessageError';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import CustomerSelect from './CustomerSelect';
import { PERMISSION, PERMISSION_FLAG } from '../../../constants/permissions';
import { can } from '../../../utils/permissions';
import {
  isConflictError,
  setRoomCustomer,
} from '../../../services/customers/customersApi';

const WallSetupForm = ({
  open,
  onClose,
  onApply,
  initialSceneData,
  initialAppearance,
  texturesCatalog,
  selectedRoomId,
  initialRoomData,
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const user = useSelector(state => state.auth.user);
  // Chọn khách cần đọc danh sách (view) và gắn vào room (edit)
  const canAttachCustomer =
    can(user, PERMISSION.CUSTOMERS, PERMISSION_FLAG.VIEW) &&
    can(user, PERMISSION.CUSTOMERS, PERMISSION_FLAG.EDIT);
  const sceneRef = useRef(null);
  const [selectedMeshInfo, setSelectedMeshInfo] = useState(null);

  const changeTimerRef = useRef(null);

  const floorRepeat = initialAppearance?.floorRepeat ?? 4;
  const [isLoading, setIsLoading] = useState(false);
  const containerRef = useRef(null);

  const validatorSize = ({ value, min, max }) => {
    const type = getInputType(value);
    if (type === 'text') {
      return Promise.reject(new Error(t('Invalid size')));
    }
    if (['number', 'invalid'].includes(type)) {
      const num = (parseFloat(value) || 0) / 1000;
      if (min && max) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min * 1000}mm ${t('to')} ${max * 1000}mm`)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid size'));
        }
      }
    }
    if (type === 'expression') {
      const num = evaluateExpression(value) / 1000;
      if (min && max) {
        if (num > max || num < min) {
          return Promise.reject(
            new Error(`${min * 1000}mm ${t('to')} ${max * 1000}mm`)
          );
        }
      } else {
        if (num <= 0) {
          return Promise.reject(t('Invalid size'));
        }
      }
    }
    return Promise.resolve();
  };

  useEffect(() => {
    if (open) {
      const scene = new SceneManager(
        'canvas-wall',
        meshInfo => {
          setSelectedMeshInfo(meshInfo);
        },
        ({ width, depth, height }) => {
          form.setFieldsValue({
            width: roundTo(width * 1000, 0),
            depth: roundTo(depth * 1000, 0),
            height: roundTo(height * 1000, 0),
          });
        }
      );

      scene.initialize({ type: 'wall', containerId: 'container__canvas-wall' });
      const { width, depth } = scene.getFloorSize();
      // setFloorSize({ width: width * 1000, depth: depth * 1000 });
      sceneRef.current = scene;

      // Đồng bộ lại UI theo initialAppearance mỗi lần mở
      // setFloorTexId(initialAppearance?.floorTexId || texturesCatalog?.[0]?.id);
      // setWallColor((initialAppearance?.wallColor || "#D0CABF").toUpperCase());
      form.setFieldsValue({
        roomName: initialRoomData?.name || '',
        customerId: initialRoomData?.customer_id ?? undefined,
        width: width * 1000,
        depth: depth * 1000,
        height: (scene.wallHeight || 3) * 1000,
        floorTexId: initialAppearance?.floorTexId || texturesCatalog?.[0]?.id,
        wallColor: (initialAppearance?.wallColor || '#D0CABF').toUpperCase(),
      });
      // Room đã lưu: kích thước theo bản ghi room, rồi đến scene đã lưu (nếu có) vì scene mới dựng chỉ có mặc định
      if (initialRoomData?.width_floor && initialRoomData?.depth_floor) {
        form.setFieldsValue({
          width: roundTo(initialRoomData.width_floor * 1000, 0),
          depth: roundTo(initialRoomData.depth_floor * 1000, 0),
          ...(initialRoomData.height_wall && {
            height: roundTo(initialRoomData.height_wall * 1000, 0),
          }),
        });
      }
      if (initialSceneData) applySavedScene(scene, initialSceneData);
      // Apply appearance ban đầu cho scene form
      const tex0 = texturesCatalog?.find(
        value =>
          value.id ===
          (initialAppearance?.floorTexId || form.getFieldValue('floorTexId'))
      );
      if (tex0)
        scene.updateFloorTextureFromImage(tex0.img, { repeatBy: floorRepeat });
      scene.updateWallColor(
        initialAppearance?.wallColor || form.getFieldValue('wallColor')
      );

      const dragController = new DragController({
        camera: scene.camera,
        canvas: scene.canvas,
        controls: scene.controls,
        getDraggableMeshes: () => [
          ...scene.columns.map(col => col.mesh),
          ...scene.beams.map(beam => beam.mesh),
        ],

        getFloorSize: () => scene.getFloorSize(),
        getSelectedMesh: () => scene.selectedMesh,
        getDraggedSize: mesh => {
          const column = scene.columns.find(col => col.mesh === mesh);
          if (column) return column.size;

          const beam = scene.beams.find(b => b.mesh === mesh);
          if (beam) return beam.size;

          return [0.3, 3, 0.3]; // fallback
        },

        getWallThickness: () => scene.wallThickness,
      });

      return () => {
        dragController.dispose();
        scene.dispose();
      };
    } else {
      // scene.dispose();
      if (sceneRef.current !== null) {
        sceneRef.current.dispose();
        sceneRef.current = null;
      }
    }
  }, [open, initialRoomData]);

  // Đổ scene đã lưu (tường, cột, dầm, kích thước sàn, chiều cao) và màu/texture vào scene của form
  const applySavedScene = useCallback(
    (scene, sceneData) => {
      scene.importSceneData(sceneData);

      const { width, depth } = scene.getFloorSize();
      form.setFieldsValue({
        width: width * 1000,
        depth: depth * 1000,
        height: (scene.wallHeight || 3) * 1000,
      });
      // Re-apply texture  color theo UI hiện tại
      const tex = texturesCatalog?.find(
        value =>
          value.id ===
          (initialAppearance?.floorTexId || form.getFieldValue('floorTexId'))
      );
      if (tex)
        scene.updateFloorTextureFromImage(tex.img, { repeatBy: floorRepeat });
      scene.updateWallColor(
        initialAppearance?.wallColor || form.getFieldValue('wallColor')
      );
    },
    [form, texturesCatalog, initialAppearance, floorRepeat]
  );

  useEffect(() => {
    if (initialSceneData && sceneRef.current) {
      applySavedScene(sceneRef.current, initialSceneData);
    }
  }, [initialSceneData]);

  useEffect(() => {
    if (selectedMeshInfo) {
      form.setFieldsValue({
        meshWidth: roundTo(
          (selectedMeshInfo.width ? parseFloat(selectedMeshInfo.width) : 0) *
            1000
        ),
        meshDepth: roundTo(
          (selectedMeshInfo.depth ? parseFloat(selectedMeshInfo.depth) : 0) *
            1000,
          0
        ),
        meshHeight: roundTo(
          (selectedMeshInfo.height ? parseFloat(selectedMeshInfo.height) : 0) *
            1000,
          0
        ),
        distanceToWallFront: roundTo(
          parseFloat(selectedMeshInfo?.distanceToWallFront * 1000),
          0
        ),
        distanceToWallLeft: roundTo(
          parseFloat(selectedMeshInfo?.distanceToWallLeft * 1000),
          0
        ),
      });
      if (containerRef.current) {
        containerRef.current.scrollTop = containerRef.current.scrollHeight;
      }
    } else {
      sceneRef?.current?.clearAllCornerPoints?.();
    }
  }, [selectedMeshInfo]);

  useEffect(() => {
    const onKey = async e => await handleDeleteKeyPress(e);
    document.addEventListener('keydown', onKey);

    return () => {
      document.removeEventListener('keydown', onKey);
    };
  }, [sceneRef, selectedMeshInfo]);

  const handleDeleteKeyPress = useCallback(
    async event => {
      if (
        (event.key !== 'Delete' && event.key !== 'Backspace') ||
        !selectedMeshInfo?.mesh ||
        !sceneRef.current
      )
        return;
      const activeElement = document.activeElement;
      const isTyping =
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        activeElement.isContentEditable;

      if (isTyping) {
        return; // Không làm gì, để người dùng xóa text bình thường
      }

      if (selectedMeshInfo?.mesh) {
        sceneRef.current.removeDistanceArrows();
        sceneRef.current.removeColumn(selectedMeshInfo.mesh);
        sceneRef.current.removeBeam(selectedMeshInfo.mesh);
        sceneRef.current.scene.remove(selectedMeshInfo.mesh);
      }
    },

    [selectedMeshInfo]
  );

  const handleAddColumn = () => {
    if (sceneRef.current && sceneRef.current.addColumn) {
      sceneRef.current.addColumn();
    }
  };

  const handleChange = (key, rawValue) => {
    const normalizedValue =
      (parseFloat(String(rawValue).replace(',', '.')) || 0) / 1000;
    if (key === 'height') {
      // setWallHeight(normalizedValue * 1000);
      sceneRef.current?.updateWallHeight(normalizedValue);
    } else {
      const { width, depth } = form.getFieldsValue(['width', 'depth']);
      const newSize = {
        ...{ width: width / 1000, depth: depth / 1000 },
        [key]: normalizedValue,
      };
      // setFloorSize({ ...floorSize, [key]: normalizedValue * 1000 });
      sceneRef.current?.updateFloorSize(newSize.width, newSize.depth);
    }
  };

  const handleAddBeamWall1 = () => {
    sceneRef.current?.addBeamForWall1();
  };

  const handleAddBeamWall2 = () => {
    sceneRef.current?.addBeamForWall2();
  };

  const items = [
    {
      img: '/assets/images/column.png',
      label: t('Column'),
      onClick: handleAddColumn,
    },
    {
      img: '/assets/images/beam1.png',
      label: t('Beam') + ' 1',
      onClick: handleAddBeamWall1,
    },
    {
      img: '/assets/images/beam2.png',
      label: t('Beam') + ' 2',
      onClick: handleAddBeamWall2,
    },
  ];

  const handleEditMeshSize = (key, rawValue) => {
    const floatVal = parseFloat(rawValue) / 1000;
    // setEditingMeshSize((prev) => ({
    //   ...prev,
    //   [key]: floatVal,
    // }));

    if (!isNaN(floatVal)) {
      sceneRef.current?.updateMeshSize(key, floatVal);
    }
  };

  // // Helper: dataURL -> Blob
  // function dataURLtoBlob(dataURL) {
  //   const arr = dataURL.split(",");
  //   const mime = arr[0].match(/:(.*?);/)[1];
  //   const bstr = atob(arr[1]);
  //   let n = bstr.length;
  //   const u8arr = new Uint8Array(n);
  //   while (n--) u8arr[n] = bstr.charCodeAt(n);
  //   return new Blob([u8arr], { type: mime });
  // }

  // // Helper: chụp ảnh canvas làm thumbnail (JPEG nhẹ)
  // async function getCanvasThumbnail(canvasId = "canvas-wall", quality = 0.9) {
  //   const canvas = document.getElementById(canvasId);
  //   if (!canvas) return null;
  //   const dataURL = canvas.toDataURL("image/jpeg", quality);
  //   return dataURLtoBlob(dataURL);
  // }

  const handleConfirm = async values => {
    setIsLoading(true);
    try {
      if (!sceneRef.current) throw new Error(t('Scene not ready'));

      // 1) Export scene
      const sceneData = sceneRef.current.exportSceneData();

      // 2) Lấy thumbnail từ canvas
      const thumbBlob = await captureRoomScreenshot(
        sceneRef.current,
        'canvas-wall',
        {
          quality: 0.92,
          format: 'image/jpeg',
          square: true,
          width: 1200,
          height: 1200,
        }
      );

      if (!thumbBlob) throw new Error(t('Failed to create thumbnail'));

      const widthFloor = values.width / 1000;
      const depthFloor = values.depth / 1000;
      const heightWall = values.height / 1000;

      const texFloorObj = texturesCatalog?.find(
        value => value.id === values.floorTexId
      );
      const textureFloor =
        texFloorObj?.key || texFloorObj?.img || String(values.floorTexId);
      const textureWall = (values.wallColor || '#D0CABF').toUpperCase();
      const { beams = [], ceiling = {}, columns = [], walls = [] } = sceneData;
      const attributes = JSON.stringify({
        beams,
        ceiling,
        columns,
        walls,
      });

      const formData = new FormData();
      formData.append('name', values.roomName.trim());
      formData.append('width_floor', String(widthFloor));
      formData.append('depth_floor', String(depthFloor));
      formData.append('height_wall', String(heightWall));
      formData.append('texture_wall', textureWall);
      formData.append('texture_floor', textureFloor);
      formData.append('room_design_data', attributes);
      formData.append('thumbnail', thumbBlob, 'room_thumbnail.jpg');

      let res;
      let roomId;
      if (selectedRoomId) {
        // UPDATE room hiện tại
        res = await callApi({
          endpoint: `rooms/v3/room/${selectedRoomId}`,
          method: 'put',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        roomId = selectedRoomId;
      } else {
        // CREATE room mới
        res = await callApi({
          endpoint: 'rooms/v3/room',
          method: 'post',
          data: formData,
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });

        // Lấy room ID từ response
        roomId = res.data?.result?.data?.id || res.data?.data?.id;

        if (!roomId) {
          throw new Error(t('Failed to get room ID from server'));
        }
      }

      if (!res.data?.success) {
        const errText = await res.text().catch(() => '');
        throw new Error(
          `API lỗi (${res?.data?.message}): ${errText || 'Unknown'}`
        );
      }

      // Gắn/gỡ khách chỉ khi người dùng đổi ô khách. Lỗi ở đây không làm hỏng việc lưu room đã xong
      let customerChanged = false;
      let customerPatch = {};
      if (canAttachCustomer && form.isFieldTouched('customerId')) {
        try {
          const updatedRoom = await setRoomCustomer(
            roomId,
            values.customerId ?? null
          );
          customerChanged = true;
          customerPatch = {
            customer_id: updatedRoom?.customer_id ?? null,
            customer_name: updatedRoom?.customer_name ?? null,
            customer_is_confirmed: updatedRoom?.customer_is_confirmed ?? false,
          };
        } catch (error) {
          openMessageError({
            content: isConflictError(error)
              ? t('Customer is confirmed. Cancel the confirmation first')
              : t('Unable to update room customer'),
          });
        }
      }

      // 5) Done: trả data lên parent với roomId
      onApply?.({
        customerChanged,
        // Những gì vừa lưu, để parent cập nhật initialRoomData cho lần mở form sau
        savedRoom: {
          id: roomId,
          name: values.roomName.trim(),
          width_floor: widthFloor,
          depth_floor: depthFloor,
          height_wall: heightWall,
          texture_wall: textureWall,
          texture_floor: textureFloor,
          ...customerPatch,
        },
        sceneData,
        appearance: {
          wallColor: values.wallColor,
          floorTexId: values.floorTexId,
          floorRepeat,
        },
        ...values,
        roomId,
        roomName: values.roomName.trim(),
        isUpdate: !!selectedRoomId,
      });

      openMessageSuccess({
        content: selectedRoomId
          ? t('Room updated successfully!')
          : t('New room created successfully!'),
      });

      // 4) Call API
      // const res = await callApi({
      //   endpoint: "rooms/v3/room",
      //   method: "post",
      //   data: formData,
      //   headers: {
      //     // 👇 Quan trọng: để axios không stringify FormData
      //     "Content-Type": "multipart/form-data",
      //   },
      // });

      // if (!res.data?.success) {
      //   const errText = await res.text().catch(() => "");
      //   throw new Error(
      //     `API lỗi (${res?.data?.message}): ${errText || "Unknown"}`
      //   );
      // }

      // // const data = await res.json().catch(() => ({}));

      // // 5) Done: trả data lên trên nếu cần
      // onApply?.({
      //   sceneData,
      //   appearance: {
      //     wallColor: values.wallColor,
      //     floorTexId: values.floorTexId,
      //     floorRepeat,
      //   },
      //   // roomName,
      //   ...values,
      //   // apiResult: res.data,
      // });
    } catch (err) {
      openMessageError({ content: err.message });
    }
    setIsLoading(false);
  };

  const handleChangeFloorTexture = id => {
    // setFloorTexId(id);
    const tex = texturesCatalog.find(value => value.id === id);
    if (tex)
      sceneRef.current?.updateFloorTextureFromImage?.(tex.img, {
        repeatBy: floorRepeat,
      });
  };

  // const handleWallColorChange = (c) => {
  //   const hex = c.toHexString(); // #RRGGBB
  //   // setWallColor(hex);
  //   sceneRef.current?.updateWallColor?.(hex);
  // };

  // const handleTestScreenshot = async () => {
  //   try {
  //     console.log("🧪 Bắt đầu test...");

  //     const blob = await captureRoomScreenshot(
  //       sceneRef.current,
  //       "canvas-wall",
  //       {
  //         quality: 0.95,
  //         format: "image/png",
  //         square: true,
  //         width: 1200,
  //         height: 1200,
  //       }
  //     );

  //     const url = URL.createObjectURL(blob);
  //     const link = document.createElement("a");
  //     link.href = url;
  //     link.download = `test-fullroom-${Date.now()}.png`;
  //     document.body.appendChild(link);
  //     link.click();
  //     document.body.removeChild(link);
  //     URL.revokeObjectURL(url);
  //   } catch (err) {
  //     console.error(" Test thất bại:", err);
  //     alert("Lỗi: " + err.message);
  //   }
  // };

  // Debounced scale handler
  const debouncedValidate = useCallback((changedValues, delay = 300) => {
    if (changeTimerRef.current) {
      clearTimeout(changeTimerRef.current);
    }

    changeTimerRef.current = setTimeout(async () => {
      const name = Object.keys(changedValues)[0];
      const value = changedValues[name];
      await form.validateFields([name]); // chỉ validate field này
      if (getInputType(changedValues[name]) === 'number') {
        try {
          if (['width', 'height', 'depth'].includes(name)) {
            handleChange(name, value);
          } else if (['meshWidth', 'meshHeight', 'meshDepth'].includes(name)) {
            handleEditMeshSize(name.replace('mesh', '').toLowerCase(), value);
          } else if (
            ['distanceToWallFront', 'distanceToWallLeft'].includes(name)
          ) {
            sceneRef.current.updateMeshDistance({
              [name]: value / 1000,
            });
          }
        } catch {
          // Ignore validation errors
        }
      } else {
        if (['floorTexId'].includes(name)) {
          handleChangeFloorTexture(value);
        } else if (['wallColor'].includes(name)) {
          sceneRef.current?.updateWallColor?.(value);
        }
      }
    }, delay);
  }, []);

  const handleKeyDown = e => {
    // Chặn Enter key trên Form
    if (e.key === 'Enter') {
      e.preventDefault();
    }
  };

  return (
    <>
      <Form
        labelCol={{ flex: '110px' }}
        labelAlign="left"
        labelWrap
        colon={false}
        form={form}
        layout="horizontal"
        initialValues={{
          width: 6000,
          depth: 6000,
          height: 3000,
        }}
        onValuesChange={changedValues => {
          debouncedValidate(changedValues);
        }}
        onFinish={handleConfirm}
        onKeyDown={handleKeyDown}
      >
        <BottomSheet
          classNames={{
            title: 'flex justify-between self-stretch items-center',
            body: '!p-0 gap-0',
          }}
          title={
            <div className="wall-titlebar">
              <ButtonIconOutlineLarge
                type="button"
                onClick={() => onClose?.()}
                icon={<Icon.ICArrowBack />}
              />

              <span className="text-headline-small wall-title">
                {t('Setup Space')}
              </span>

              <ButtonPrimaryLarge
                type="submit"
                title={t('Complete')}
                loading={isLoading}
                disabled={isLoading}
                // onClick={handleConfirm}
              />
            </div>
          }
          open={open}
          onClose={() => {}}
        >
          {open && (
            <div id="container__canvas-wall">
              <canvas id="canvas-wall" />
            </div>
          )}
          <div className="form-right-panel" ref={containerRef}>
            {
              <>
                <div className="form-right-panel-size">
                  <span className="text-title-large">{t('Information')}</span>
                  <Form.Item
                    className="roomname-item" // <— gắn class riêng để chỉ áp cho field này
                    label={t('Room name')}
                    name="roomName"
                    rules={[
                      {
                        validator: (_, value) => {
                          if (value?.length) return Promise.resolve();
                          else return Promise.reject();
                        },
                        message: t('Please enter room name'),
                      },
                    ]}
                    colon={false}
                    style={{ alignSelf: 'stretch' }}
                  >
                    <InputComponent
                      type="text"
                      size="large"
                      // value={roomName}
                      // onChange={(e) => setRoomName(e.target.value)}
                      placeholder={t('Enter room name')}
                    />
                  </Form.Item>
                  {canAttachCustomer && (
                    <Form.Item
                      label={t('Customer')}
                      name="customerId"
                      colon={false}
                      style={{ alignSelf: 'stretch' }}
                    >
                      <CustomerSelect
                        selected={
                          initialRoomData?.customer_id
                            ? {
                                id: initialRoomData.customer_id,
                                name: initialRoomData.customer_name,
                              }
                            : null
                        }
                      />
                    </Form.Item>
                  )}
                </div>
                <div className="form-right-panel-size">
                  <span className="text-title-large">{t('Perspective')}</span>
                  <Form.Item
                    label={
                      <span className="text-label-medium">
                        {t('Floor Color')}
                      </span>
                    }
                    style={{ alignSelf: 'stretch' }}
                    name={'floorTexId'}
                    on
                  >
                    <SelectComponent
                      size="large"
                      className={'ps-4'}
                      // value={floorTexId}
                      // onChange={handleChangeFloorTexture}
                      options={[
                        ...texturesCatalog.map(item => ({
                          value: item.id,
                          label: (
                            <div className="flex gap-8 items-center">
                              <span
                                className="thumb w-30 h-30"
                                style={{ backgroundImage: `url(${item.img})` }}
                              />
                              {item.label}
                            </div>
                          ),
                        })),
                      ]}
                    />
                  </Form.Item>

                  {/* WALL COLOR – ColorPicker panel lớn + Hex input */}
                  <Form.Item
                    label={
                      <span className="text-label-medium">
                        {t('Wall Color')}
                      </span>
                    }
                    style={{ alignSelf: 'stretch' }}
                  >
                    <div className="colorpicker-wrap">
                      <Form.Item
                        name="wallColor"
                        getValueFromEvent={color =>
                          color?.toHexString()?.toUpperCase()
                        }
                      >
                        <ColorPicker
                          format="hex"
                          disabledAlpha
                          size="large"
                          // showText={(color) =>
                          //   color?.toHexString()?.toUpperCase()
                          // }
                          // value={wallColor}
                          // onChange={(c) => {
                          //   const hex = c.toHexString().toUpperCase(); // "#C76868"
                          //   setWallColor(hex);
                          //   sceneRef.current?.updateWallColor?.(hex);
                          // }}
                        />
                      </Form.Item>
                      <Form.Item name={'wallColor'}>
                        <InputComponent
                          // className="hex-input"
                          // value={wallColor}
                          size="large"
                          // onChange={(e) => {
                          //   const v = e.target.value.toUpperCase();
                          //   setWallColor(v);
                          //   sceneRef.current?.updateWallColor?.(v);
                          // }}
                          placeholder="Hex Code"
                          maxLength={7}
                        />
                      </Form.Item>
                    </div>
                  </Form.Item>
                </div>

                <div className="form-right-panel-size">
                  <span className="text-title-large">{t('Size')}</span>
                  <Form.Item
                    label={
                      <span className="text-label-medium">
                        {t('Wall length') + ' 1'}
                      </span>
                    }
                    name={'width'}
                    rules={[
                      {
                        validator: (_, value) => {
                          return validatorSize({
                            value,
                            min: 1,
                            max: 10,
                          });
                        },
                      },
                    ]}
                    style={{ alignSelf: 'stretch' }}
                  >
                    <InputComponent
                      step="10"
                      size="large"
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={t('Enter length')}
                    />
                  </Form.Item>
                  <Form.Item
                    label={
                      <span className="text-label-medium">
                        {t('Wall length') + ' 2'}
                      </span>
                    }
                    name={'depth'}
                    rules={[
                      {
                        validator: (_, value) => {
                          return validatorSize({
                            value,
                            min: 1,
                            max: 10,
                          });
                        },
                      },
                    ]}
                    style={{ alignSelf: 'stretch' }}
                  >
                    <InputComponent
                      step="10"
                      size="large"
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={t('Enter length')}
                    />
                  </Form.Item>
                  <Form.Item
                    label={
                      <span className="text-label-medium">
                        {t('Wall Height')}
                      </span>
                    }
                    name={'height'}
                    rules={[
                      {
                        validator: (_, value) => {
                          return validatorSize({
                            value,
                            min: 1,
                            max: 10,
                          });
                        },
                      },
                    ]}
                    style={{ alignSelf: 'stretch' }}
                  >
                    <InputComponent
                      step="10"
                      size="large"
                      addonAfter={<span className="text-body-medium">mm</span>}
                      placeholder={t('Enter height')}
                    />
                  </Form.Item>
                </div>
                <div className="form-right-panel-structure">
                  <span className="text-title-large">{t('Structure')}</span>
                  <span className="text-body-medium">
                    {t(
                      'You can skip this step if your wall has no columns or you feel it is unnecessary.'
                    )}
                  </span>
                  <div className="form-group">
                    <span className="group-title">{t('Structure')}</span>
                    <div className="structure-options">
                      {items.map((item, i) => {
                        return (
                          <CustomProductCard
                            onClick={item.onClick}
                            img={item.img}
                            label={item.label}
                            key={i}
                          />
                        );
                      })}
                    </div>
                  </div>
                </div>
              </>
            }

            {selectedMeshInfo && (
              <div className="property-form">
                <span className="text-title-large">{t('Properties')}</span>
                <span className="text-body-medium">
                  {t(
                    'You can skip this step if your wall has no columns or you feel it is unnecessary.'
                  )}
                </span>
                <div className="form-group">
                  <span className="group-title">{t('Structure')}</span>
                  <div className="structure-options">
                    <div className="form-right-panel-size">
                      <Form.Item
                        label={t('Width')}
                        className="self-stretch"
                        name={'meshWidth'}
                        rules={[
                          {
                            validator: (_, value) => {
                              return validatorSize({
                                value,
                                min: 0.0001,
                                max: 10,
                              });
                            },
                          },
                        ]}
                        onClick={() => {
                          if (selectedMeshInfo?.mesh) {
                            sceneRef?.current?.clearAllCornerPoints?.();
                            sceneRef?.current?.generateCornerPoints(
                              selectedMeshInfo.mesh,
                              'x',
                              false
                            );
                          }
                        }}
                      >
                        <InputComponent
                          step="10"
                          size="large"
                          placeholder={t('Enter width')}
                          addonAfter={
                            <span className="text-body-medium">mm</span>
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label={t('Depth')}
                        className="self-stretch"
                        name={'meshDepth'}
                        rules={[
                          {
                            validator: (_, value) => {
                              return validatorSize({
                                value,
                                min: 0.0001,
                                max: 10,
                              });
                            },
                          },
                        ]}
                        onClick={() => {
                          if (selectedMeshInfo?.mesh) {
                            sceneRef?.current?.clearAllCornerPoints?.();
                            sceneRef?.current?.generateCornerPoints(
                              selectedMeshInfo.mesh,
                              'z',
                              false
                            );
                          }
                        }}
                      >
                        <InputComponent
                          size="large"
                          step="10"
                          placeholder={t('Enter depth')}
                          addonAfter={
                            <span className="text-body-medium">mm</span>
                          }
                        />
                      </Form.Item>
                      <Form.Item
                        label={t('Height')}
                        className="self-stretch"
                        name={'meshHeight'}
                        rules={[
                          {
                            validator: (_, value) => {
                              return validatorSize({
                                value,
                                min: 0.0001,
                                max: 10,
                              });
                            },
                          },
                        ]}
                        onClick={() => {
                          if (selectedMeshInfo?.mesh) {
                            sceneRef?.current?.clearAllCornerPoints?.();
                            sceneRef?.current?.generateCornerPoints(
                              selectedMeshInfo.mesh,
                              'y',
                              false
                            );
                          }
                        }}
                      >
                        <InputComponent
                          size="large"
                          step="10"
                          placeholder={t('Enter height')}
                          addonAfter={
                            <span className="text-body-medium">mm</span>
                          }
                        />
                      </Form.Item>
                      <Divider type="horizontal" className="m-0 !p-0" />
                      <Row gutter={[16]}>
                        <Col span={12}>
                          <Form.Item
                            labelCol={{ flex: 0 }}
                            layout="vertical"
                            label={t('Distance to Wall') + ' 1'}
                            name={'distanceToWallFront'}
                            onClick={() => {
                              sceneRef.current.removeDistanceArrowLeftAndRight();
                              if (selectedMeshInfo.mesh)
                                sceneRef.current.showDistanceArrowToWallFrontAndBack(
                                  selectedMeshInfo.mesh
                                );
                            }}
                          >
                            <InputComponent
                              size="large"
                              placeholder={t('Enter distance')}
                              addonAfter={
                                <span className="text-body-medium">mm</span>
                              }
                            />
                          </Form.Item>
                        </Col>
                        <Col span={12}>
                          <Form.Item
                            labelCol={{ flex: 0 }}
                            layout="vertical"
                            label={t('Distance to Wall') + ' 2'}
                            name={'distanceToWallLeft'}
                            onClick={() => {
                              sceneRef.current.removeDistanceArrowFrontAndBack();
                              if (selectedMeshInfo.mesh)
                                sceneRef.current.showDistanceArrowToWallLeftAndRight(
                                  selectedMeshInfo.mesh
                                );
                            }}
                          >
                            <InputComponent
                              size="large"
                              placeholder={t('Enter distance')}
                              addonAfter={
                                <span className="text-body-medium">mm</span>
                              }
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </BottomSheet>
      </Form>
    </>
  );
};

export default WallSetupForm;
